import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Icon from "@/v2-ui/icon"
import Link from "@/v2-backoffice-client/components/link"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { useCustomerPricingQuery } from "@/v2-backoffice-client/customer/pricing/customer.pricing.query"
import {
  describePricingDeal,
  discountPercentOf,
  isOffList,
  PRICING_DEAL_KIND,
  summarizeCustomerPricing,
  type CustomerPricingLine,
} from "@/v2-backoffice-client/customer/pricing/customer.pricing.utils"
import { formatPriceInterval } from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"

export type CustomerPricingProps = {
  customerId: number
  /** The customer's Fenerum account, for the "change it in Fenerum" link. */
  billingAccountUuid?: string | null
  className?: string
}

// The customer panel's "What they pay" section, under the licenses: every
// live Fenerum subscription behind them with the list price next to what
// is actually billed, and in plain words why they differ — a deal on the
// subscription, a contract on the account (price bands, binding period), or
// nothing on record. Every figure is Fenerum's, mirrored hourly; the
// section explains, it never sets a price. Renders nothing for a customer
// with no live subscriptions (trials and operator grants have none).
function CustomerPricing(props: CustomerPricingProps) {
  const { customerId, billingAccountUuid, className } = props

  const { data, loading, error } = useCustomerPricingQuery({
    variables: { customerId },
  })
  const lines = data?.getCustomerPricing ?? []
  const summary = summarizeCustomerPricing(lines)

  if (!loading && !error && lines.length === 0) return null

  return (
    <section
      className={cn("customer-pricing flex flex-col gap-2", className)}
      data-feature-code="UI_BackofficeCustomer_Pricing_Section"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold">What they pay</h3>
        <span className="text-xs text-muted-foreground">
          List price against what Fenerum bills, per subscription. Prices and
          deals are changed in Fenerum; this reads them back within the hour.
        </span>
        {billingAccountUuid && (
          <Link
            variant="muted"
            size="xs"
            iconSrc="bank"
            iconSize={10}
            external
            to={`${FENERUM_ACCOUNT_BASE_URL}/${billingAccountUuid}/`}
            className="underline underline-offset-2"
          >
            Open the account in Fenerum
          </Link>
        )}
      </div>

      {/* Partial data renders; the banner only when nothing came back. */}
      {error && lines.length === 0 && (
        <ErrorMessage className="text-left" error={error} />
      )}

      {summary && <CustomerPricingSummary summary={summary} />}

      {loading && lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading prices…</p>
      ) : lines.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">License</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">List price</th>
                <th className="px-3 py-2 text-right font-medium">They pay</th>
                <th className="px-3 py-2 font-medium">Why</th>
                <th className="px-3 py-2 font-medium">Renews</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <CustomerPricingRow key={line.SubscriptionId} line={line} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

type CustomerPricingSummaryProps = {
  summary: NonNullable<ReturnType<typeof summarizeCustomerPricing>>
}

// The headline: a month of billing against the same lines at list.
function CustomerPricingSummary(props: CustomerPricingSummaryProps) {
  const { summary } = props
  const gap = summary.billedMonthly - summary.listMonthly
  const gapPercent =
    summary.listMonthly > 0 ? (gap / summary.listMonthly) * 100 : null
  const isSame = Math.abs(gap) < 0.5

  return (
    <p className="text-sm">
      <span className="font-medium tabular-nums">
        {formatInvoiceAmount(summary.billedMonthly, summary.currency)}/month
      </span>{" "}
      <span className="text-muted-foreground">
        {isSame
          ? "billed, at list price."
          : `billed against ${formatInvoiceAmount(summary.listMonthly, summary.currency)} at list${
              gapPercent != null
                ? ` (${gapPercent > 0 ? "+" : "−"}${formatNumber(Math.abs(gapPercent), 1)} %)`
                : ""
            }.`}
        {summary.skipped > 0 &&
          ` ${summary.skipped} ${summary.skipped === 1 ? "line" : "lines"} without a list price left out.`}
      </span>
    </p>
  )
}

type CustomerPricingRowProps = {
  line: CustomerPricingLine
}

function CustomerPricingRow(props: CustomerPricingRowProps) {
  const { line } = props
  const deal = describePricingDeal(line)
  const interval = formatPriceInterval(line.IntervalType, line.IntervalCount)
  const offList = isOffList(line)
  const percent = discountPercentOf(line)
  const quantity = line.Quantity ?? 1
  const unit = line.SeatSourceCode === 1 ? "seat" : "unit"

  return (
    <tr className="border-b align-top last:border-b-0">
      <td className="px-3 py-2">
        {line.PlanId ? (
          <Link to={`>>?v=plan:${line.PlanId}`}>
            {line.PlanName ?? "Unnamed license"}
          </Link>
        ) : (
          (line.PlanName ?? "Unnamed license")
        )}
        {line.ExternalStatus && line.ExternalStatus !== "active" && (
          <span className="ml-1.5 text-xs text-muted-foreground">
            {line.ExternalStatus === "not_renewing"
              ? "not renewing"
              : line.ExternalStatus}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{quantity}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {line.ListPrice != null ? (
          <>
            {formatInvoiceAmount(line.ListPrice, line.Currency)}
            <span className="text-muted-foreground">{interval}</span>
            {quantity > 1 && line.ListUnitPrice != null && (
              <div className="text-xs text-muted-foreground">
                {quantity} × {formatNumber(line.ListUnitPrice)} per {unit}
              </div>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {line.BilledPrice != null ? (
          <>
            <span className={cn(offList && "font-medium")}>
              {formatInvoiceAmount(line.BilledPrice, line.Currency)}
            </span>
            <span className="text-muted-foreground">{interval}</span>
            {offList && percent != null && (
              <div className="text-xs text-muted-foreground">
                {percent > 0 ? "+" : "−"}
                {formatNumber(Math.abs(percent), 1)} % vs list
              </div>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <CustomerPricingDeal deal={deal} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
        {line.NextRenewalDate ? formatDateString(line.NextRenewalDate) : "—"}
      </td>
    </tr>
  )
}

type CustomerPricingDealProps = {
  deal: ReturnType<typeof describePricingDeal>
}

// The Why cell: one line, then the facts behind it. An unexplained gap is
// amber — a conversation to have, not broken plumbing.
function CustomerPricingDeal(props: CustomerPricingDealProps) {
  const { deal } = props
  const isUnexplained = deal.kind === PRICING_DEAL_KIND.Unexplained

  return (
    <div className="flex flex-col gap-0.5">
      {deal.kind === PRICING_DEAL_KIND.List ? (
        <span className="text-muted-foreground">{deal.summary}</span>
      ) : isUnexplained ? (
        <DotText variant="warning">{deal.summary}</DotText>
      ) : (
        <span className="flex items-start gap-1.5">
          <Icon
            src={
              deal.kind === PRICING_DEAL_KIND.Contract
                ? "file-signature"
                : "badge-percent"
            }
            size={12}
            className="mt-0.5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <span>{deal.summary}</span>
        </span>
      )}
      {deal.details.map((detail) => (
        <span key={detail} className="text-xs text-muted-foreground">
          {detail}
        </span>
      ))}
    </div>
  )
}

export default CustomerPricing
